import React, { createContext, useContext, useCallback, useRef, useState } from "react";

const MAX_TRACE_SIZE = 10;

export interface ActionTraceEntry {
  timestamp: string;
  action: string;
  target: string;
  module?: string;
  context?: Record<string, unknown>;
}

export interface EntityInfo {
  type?: string;
  id?: string;
  name?: string;
}

export interface ErrorTrackerContextValue {
  currentModule: string | null;
  currentAction: string | null;
  entityInfo: EntityInfo;
  actionTrace: ActionTraceEntry[];
  setCurrentModule: (module: string) => void;
  setCurrentAction: (
    action: string,
    entityType?: string,
    entityId?: string,
    entityName?: string
  ) => void;
  trackAction: (
    action: string,
    target: string,
    context?: Record<string, unknown>
  ) => void;
  getErrorContext: () => {
    module: string | null;
    userAction: string | null;
    entityType: string | undefined;
    entityId: string | undefined;
    entityName: string | undefined;
    actionTrace: ActionTraceEntry[];
  };
}

const ErrorTrackerContext = createContext<ErrorTrackerContextValue | null>(null);

export function ErrorTrackerProvider({ children }: { children: React.ReactNode }) {
  const [currentModule, setCurrentModuleState] = useState<string | null>(null);
  const [currentAction, setCurrentActionState] = useState<string | null>(null);
  const [entityInfo, setEntityInfo] = useState<EntityInfo>({});
  const actionTraceRef = useRef<ActionTraceEntry[]>([]);

  const setCurrentModule = useCallback((module: string) => {
    setCurrentModuleState(module);
  }, []);

  const setCurrentAction = useCallback(
    (
      action: string,
      entityType?: string,
      entityId?: string,
      entityName?: string
    ) => {
      setCurrentActionState(action);
      setEntityInfo({
        type: entityType,
        id: entityId,
        name: entityName,
      });
    },
    []
  );

  const trackAction = useCallback(
    (action: string, target: string, context?: Record<string, unknown>) => {
      const entry: ActionTraceEntry = {
        timestamp: new Date().toISOString(),
        action,
        target,
        module: currentModule || undefined,
        context,
      };

      // Circular buffer: keep only last MAX_TRACE_SIZE entries
      actionTraceRef.current = [
        ...actionTraceRef.current.slice(-(MAX_TRACE_SIZE - 1)),
        entry,
      ];
    },
    [currentModule]
  );

  const getErrorContext = useCallback(() => {
    return {
      module: currentModule,
      userAction: currentAction,
      entityType: entityInfo.type,
      entityId: entityInfo.id,
      entityName: entityInfo.name,
      actionTrace: [...actionTraceRef.current],
    };
  }, [currentModule, currentAction, entityInfo]);

  const value: ErrorTrackerContextValue = {
    currentModule,
    currentAction,
    entityInfo,
    actionTrace: actionTraceRef.current,
    setCurrentModule,
    setCurrentAction,
    trackAction,
    getErrorContext,
  };

  return (
    <ErrorTrackerContext.Provider value={value}>
      {children}
    </ErrorTrackerContext.Provider>
  );
}

export function useErrorTrackerContext(): ErrorTrackerContextValue {
  const context = useContext(ErrorTrackerContext);
  if (!context) {
    throw new Error(
      "useErrorTrackerContext must be used within an ErrorTrackerProvider"
    );
  }
  return context;
}

// Safe version that doesn't throw - for use in error logger where context might not exist
export function useErrorTrackerContextSafe(): ErrorTrackerContextValue | null {
  return useContext(ErrorTrackerContext);
}
